import { WSList, WSText } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryCustomerEntity } from "../../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryVerifications } from "../../../../query/onboarding/queries/useQueryVerifications";
import { useQueryUserLocation } from "../../../../query/user";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { FormAccountType } from "../../components/FormAccountType";
import { getProfileDataAccountType } from "../../components/FormAccountType/getProfileDataAccountType";
import { useOnboardignModules, useOnboardingContext } from "../../Context";
import { OnboardingModuleCreateAccount } from "../../types";
import { CreateAccountContext } from "./types";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryUserProfileV2 } from "../../../../query/user/queries/useQueryUserProfile";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

export const AccountType: React.FC<FlowStep> = ({ onNext }) => {
  const [context, setContext] = useOnboardingContext<CreateAccountContext>();
  const locationQuery = useQueryUserLocation();

  const modules = useOnboardignModules();
  const onboardingModule = modules.find(m => m.type === "create_account") as
    | OnboardingModuleCreateAccount
    | undefined;
  const accountTypes = onboardingModule?.options?.accountTypes;
  const verificationLevel = onboardingModule?.options?.verificationLevel;
  const forcedCountry = onboardingModule?.options?.country;

  const verificationLevelIsBanking = verificationLevel === "Banking";

  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);
  const queryCustomerEntity = useQueryCustomerEntity();
  const queryVerifications = useQueryVerifications();
  const queryUserProfile = useQueryUserProfileV2();
  const verificationStatus =
    verificationLevel === "Banking"
      ? queryVerifications.data?.banking
      : queryVerifications.data?.tax;

  const defaultData = useMemo(() => {
    const profileData = getProfileDataAccountType(
      queryCustomerEntity.data,
      queryMember.data,
      queryUserProfile.data
    );

    if (forcedCountry) {
      profileData.country = forcedCountry;
    } else if (!profileData.country) {
      profileData.country = locationQuery.data?.isoCode;
    }

    return profileData;
  }, [
    queryCustomerEntity.data,
    queryMember.data,
    locationQuery.data?.isoCode,
    queryUserProfile.data,
    forcedCountry
  ]);

  return (
    <WSList gap="2XL">
      <FormAccountType
        accountTypes={accountTypes}
        verificationLevelIsBanking={verificationLevelIsBanking}
        disabled={verificationStatus === "Verified"}
        countryDisabled={!!forcedCountry}
        defaultValues={context.accountType || defaultData}
        onSubmit={data => {
          setContext(prev => ({
            ...prev,
            accountType: data
          }));
          onNext?.();
        }}
        onChange={data => {
          setContext(prev => ({
            ...prev,
            accountType: data
          }));
        }}
      />

      <WSText.ParagraphXs color="gray400">
        This information is required to set up your account. If you exit now,
        you'll return to this page when you sign in again.
      </WSText.ParagraphXs>
    </WSList>
  );
};
